import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addProductToOrder } from '../../store/slices/orderSlice';
import { showAlert } from '../../store/slices/alertSlice';
import { setLoadSuccess, setLoadFailure } from '../../store/slices/loadSlice';
import { useFetchTodaysLoad } from './vanuser.hooks';
import ZXingScanner from '../../components/scanner/index';
import beepSound from '../../assets/product-success_voice.mp3';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BarcodeReaderIcon from '@mui/icons-material/BarcodeReader';
import CloseIcon from '@mui/icons-material/Close';
import {
  CircularProgress,
  Box,
  Button,
  TextField,
  Modal,
  Typography,Stack
} from '@mui/material';
import { constants } from '../../constants';

export default function AddProduct({ vanid }) {
  const [productCode, setProductCode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isBarcodeSearching, setBarcodeSearching] = useState(false);
  const [scannerEnable, setScannerEnable] = useState(false);
  const [scannedValue, setScannedValue] = useState('');
  const dispatch = useDispatch();
  const triggerSearchRef = useRef(false);

  const scannerRef = useRef(null);
  const { loadItems: loadDetails } = useSelector((state) => state.load);
  const { refetch } = useFetchTodaysLoad({ enabled: false });
  const hasLoad = Array.isArray(loadDetails) && loadDetails.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      if (!hasLoad) {
        const result = await refetch();
        if (result.status === 'success' && result.data) {
          dispatch(setLoadSuccess(result.data));
        } else if (result.status === 'error') {
          dispatch(setLoadFailure(result.error?.message || 'Failed to fetch load' ));
          dispatch(showAlert({ severity: 'error', title: result.error?.message || 'Failed to fetch load' }));
        }
      }
    };
    fetchData();
  }, [hasLoad, refetch, dispatch]);

  useEffect(() => {
    if (triggerSearchRef.current && productCode) {
      triggerSearchRef.current = false;
      handleFetchProduct();
    }
  }, [productCode]);

  const handleFetchProduct = () => {
    if (!productCode.trim()) {
      dispatch(showAlert({ severity: 'warning', title: 'Please enter a product code.' }));
      return;
    }

    if (!loadDetails?.length) {
      dispatch(showAlert({ severity: 'error', title: 'No load details available.' }));
      return;
    }

    setIsSearching(true);

      const matchedProduct = loadDetails.find(
        (item) => item.productcode?.toLowerCase() === productCode.trim().toLowerCase()
      );

      if (matchedProduct) {
        dispatch(addProductToOrder(matchedProduct));
      new Audio(beepSound).play();
      dispatch(showAlert({ severity: 'success', title: 'Product added.' }));
      setProductCode('');
    } else {
        dispatch(showAlert({ severity: 'error', title: 'Product not found in today\'s load.' }));
      }

      setIsSearching(false);
  };

  const handleFetchScannedProduct = () => {
    setScannerEnable(true);
  };

  const fetchProductFromBarcodeScanner=()=>{
    setBarcodeSearching(prev => !prev);  // toggles true/false
  }
  const handleCloseScanner = () => {
    setScannerEnable(false);
    scannerRef.current?.stopCamera();
  };


  const getItemCodeFromBarcode = (barcode) => {
    const product = loadDetails.find((item) => item.barcode === barcode);
    if (product) {
      triggerSearchRef.current = true;
      setProductCode(product.productcode);
      setScannedValue('');
    } else {
      dispatch(showAlert({ severity: 'error', title: 'Product not found for scanned barcode.' }));
    }
  };

  return (
    <>
      <Stack direction="row" spacing={2} mt={2} alignItems="stretch">
      <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    backgroundColor: '#f5f7fa', // light background
    padding: '8px 12px',
    borderRadius: 2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // subtle shadow
  }}
>
      <TextField
         placeholder="Product Code"
        value={productCode}
        onChange={(e) => setProductCode(e.target.value)}
        disabled={isSearching}
    variant="outlined"
    size="small"
    sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: 1 }}
      />
      <Button
        onClick={handleFetchProduct}
    variant="contained"
    size="small"
        disabled={isSearching}
    startIcon={
      isSearching ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        <AddShoppingCartIcon />
      )
    }
      >
    Add
        </Button>
</Box>

<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    backgroundColor: '#f5f7fa', // light background
    padding: '8px 12px',
    borderRadius: 2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // subtle shadow
  }}
>

        <Button
          onClick={handleFetchScannedProduct}
          sx={{ padding: '5px 10px' }}
          variant="contained"
          disabled={isSearching}
          startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <CameraAltIcon />}
        >
      </Button>
</Box>

      </Stack>

      <Stack direction="row" spacing={2} mt={2} alignItems="stretch">
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    backgroundColor: '#f5f7fa', // light background
    padding: '8px 12px',
    borderRadius: 2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // subtle shadow
  }}
  onMouseDown={(e) => e.preventDefault()}
>
        <Button
          onClick={fetchProductFromBarcodeScanner}
          sx={{ padding: '5px 10px' }}
          variant="contained"
          disabled={false}
          startIcon={isBarcodeSearching ? <CloseIcon />: <BarcodeReaderIcon />}
        >
        </Button>

  {isBarcodeSearching && (
  <Box
    component="form"
    onSubmit={(e) => {
      e.preventDefault();
      const value = scannedValue.trim();
      if (!value) return;
      getItemCodeFromBarcode(value);
      setScannedValue('');
  }}
    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
  >
    <TextField
      placeholder="Barcode"
      value={scannedValue}
      onChange={(e) => setScannedValue(e.target.value)}
  variant="outlined"
  size="small"
  autoFocus
      inputMode="numeric"
  onBlur={() => {
    setTimeout(() => setBarcodeSearching(false), 100);
  }}
/>
  </Box>
)}
    </Box>
      </Stack>
      {/* Modal for Scanner */}
      <Modal open={scannerEnable} onClose={handleCloseScanner}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: 'white',
              p: 3,
              borderRadius: 2,
              maxWidth: '95%',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom>
              QR / Barcode Scanner
            </Typography>
            <ZXingScanner
              ref={scannerRef}
              onScan={(data) => {
               
                setScannedValue(data);
                getItemCodeFromBarcode(data);
              }}
            />
            {scannedValue && (
              <Typography mt={2}>
                ✅ Scanned: <strong>{scannedValue}</strong>
              </Typography>
            )}
            <Button
              onClick={handleCloseScanner}
              variant="outlined"
              color="secondary"
              sx={{ mt: 2, mx: 'auto', display: 'block' }}
            >
              Close Scanner
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
